import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link } from 'react-router-dom';
import {
  createTheme,
  ThemeProvider,
  CssBaseline,
  Box,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Restaurant as RestaurantIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  ExitToApp as LogoutIcon,
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import DashboardPage from './pages/DashboardPage';
import RestaurantDetails from './pages/RestaurantDetails';
import UserManagement from './pages/UserManagement';
import ReportsPage from './pages/ReportsPage';
import LoginPage from './pages/LoginPage';
import { authService } from './services/api';

// Create a theme that matches the rest of the project
const theme = createTheme({
  palette: {
    primary: {
      main: '#FF5A5F', // Main brand color
      contrastText: '#fff',
    },
    secondary: {
      main: '#00A699', // Secondary brand color
      contrastText: '#fff',
    },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        contained: {
          boxShadow: 'none',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          marginBottom: 4,
          '&.Mui-selected': {
            backgroundColor: 'rgba(255, 90, 95, 0.08)',
          },
          '&.Mui-selected:hover': {
            backgroundColor: 'rgba(255, 90, 95, 0.12)',
          },
        },
      },
    },
  },
});

const drawerWidth = 260;

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(true);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  // Check for existing session on app load
  useEffect(() => {
    const checkAuth = () => {
      const isAuth = authService.isAuthenticated();
      if (isAuth) {
        setIsLoggedIn(true);
        setUser(authService.getCurrentUser());
      }
      setLoading(false);
    };
    
    checkAuth();
  }, []);

  // Auto close drawer on small screens
  useEffect(() => {
    if (isSmallScreen) {
      setDrawerOpen(false);
    } else {
      setDrawerOpen(true);
    }
  }, [isSmallScreen]);

  const handleLogin = (userData) => {
    setIsLoggedIn(true);
    setUser(userData || authService.getCurrentUser());
  };

  const handleLogout = () => {
    authService.logout();
    setIsLoggedIn(false);
    setUser(null);
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'Restaurants', icon: <RestaurantIcon />, path: '/restaurants' },
    { text: 'Users', icon: <PeopleIcon />, path: '/users' },
    { text: 'Reports', icon: <AssessmentIcon />, path: '/reports' },
  ];

  const drawer = (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="h5" fontWeight="bold" color="primary" sx={{ letterSpacing: '0.5px' }}>
            FORKAST
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ flexGrow: 1, overflow: 'auto', mt: 2, px: 2 }}>
          <List>
            {menuItems.map((item) => (
              <ListItem 
                button 
                key={item.text} 
                component={Link} 
                to={item.path}
                sx={{
                  py: 1.5,
                  color: 'text.primary',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 90, 95, 0.08)',
                  },
                }}
              >
                <ListItemIcon sx={{ color: theme.palette.primary.main, minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text} 
                  primaryTypographyProps={{ 
                    fontSize: '0.95rem', 
                    fontWeight: 500 
                  }} 
                />
              </ListItem>
            ))}
          </List>
        </Box>
        <Box sx={{ p: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 1 }}>
            © {new Date().getFullYear()} Forkast
          </Typography>
        </Box>
      </Box>
    </>
  );

  // Use AuthLayout for authenticated routes
  const AuthLayout = ({ children }) => {
    if (!isLoggedIn) {
      return <Navigate to="/login" />;
    }

    return (
      <Box sx={{ display: 'flex' }}>
        <AppBar 
          position="fixed" 
          sx={{ 
            zIndex: (theme) => theme.zIndex.drawer + 1,
            width: { sm: drawerOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
            ml: { sm: drawerOpen ? `${drawerWidth}px` : 0 },
            transition: 'width 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms, margin 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms',
            backgroundColor: '#fff',
            color: 'text.primary',
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
          }}
          elevation={0}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, color: theme.palette.primary.main, fontWeight: 600 }}>
              App Owner Dashboard
            </Typography>
            
            <IconButton color="primary" size="large" sx={{ mr: 1 }}>
              <NotificationsIcon />
            </IconButton>

            <IconButton color="primary" size="large" sx={{ mr: 1 }}>
              <SettingsIcon />
            </IconButton>
            
            <IconButton 
              onClick={handleMenuOpen}
              size="small"
              sx={{ ml: 1 }}
            >
              <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 36, height: 36 }}>
                {user?.name?.charAt(0) || 'A'}
              </Avatar>
            </IconButton>
            
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                elevation: 2,
                sx: {
                  borderRadius: 2,
                  minWidth: 180,
                  mt: 1,
                }
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem dense sx={{ py: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={600}>{user?.name || 'Admin User'}</Typography>
              </MenuItem>
              <MenuItem dense sx={{ py: 1 }}>
                <Typography variant="body2" color="text.secondary">{user?.email || 'admin@example.com'}</Typography>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout} sx={{ py: 1.5 }}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText primary="Logout" primaryTypographyProps={{ color: 'error.main' }} />
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>
        
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: '1px solid rgba(0, 0, 0, 0.08)',
              transition: 'width 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms',
            },
            width: drawerWidth,
            flexShrink: 0,
          }}
          open={drawerOpen}
        >
          {drawer}
        </Drawer>
        
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: '1px solid rgba(0, 0, 0, 0.08)',
            },
          }}
        >
          {drawer}
        </Drawer>
        
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            ml: { sm: `${drawerWidth}px` },
            transition: 'width 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms, margin 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms',
            minHeight: '100vh',
            backgroundColor: '#f8f9fa',
          }}
        >
          <Toolbar />
          {children}
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography variant="h6">Loading...</Typography>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={
            isLoggedIn ? <Navigate to="/" /> : <LoginPage onLogin={handleLogin} />
          } />
          
          <Route path="/" element={
            <AuthLayout>
              <DashboardPage />
            </AuthLayout>
          } />
          
          <Route path="/restaurants" element={
            <AuthLayout>
              <RestaurantDetails />
            </AuthLayout>
          } />
          
          <Route path="/users" element={
            <AuthLayout>
              <UserManagement />
            </AuthLayout>
          } />
          
          <Route path="/reports" element={
            <AuthLayout>
              <ReportsPage />
            </AuthLayout>
          } />
          
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App; 